<template>
  <div class="order-grid hyphens-auto">
    <div v-if="!isEshopSet" class="col-start-1 row-span-4">
      <OrderProductImage v-if="!isEshopSet" :line-item="lineItem" />
    </div>
    <span v-if="isSubscriptionProduct" class="text-base xl:mb-2">
      {{ lineItem.children[0]?.label }}
    </span>
    <span v-else :class="{ 'col-span-2': isEshopSet }" class="text-base xl:mb-2">
      {{ lineItem.label }}
    </span>

    <template v-if="!isSetChild">
      <SelectField
        v-if="quantityChange"
        v-model="selectedQuantity"
        :disabled="!orderConfiguration[lineItem.id].selected"
        :options="quantityOptions"
        :readonly="!orderConfiguration[lineItem.id].selected"
        class="self-start"
      />
      <div
        v-else
        class="col-start-2 row-start-3 xl:col-start-auto xl:row-start-auto xl:text-center"
      >
        <span class="xl:hidden">x</span>
        <span v-if="returnItem">
          {{ returnItem?.quantity }}
        </span>
        <span v-else-if="isConfigured && orderConfiguration[lineItem.id]?.quantity">
          {{ orderConfiguration[lineItem.id]?.quantity }}
        </span>
        <span v-else>
          {{ lineItem.quantity }}
        </span>
      </div>
    </template>

    <div class="product-options col-span-2 text-sm xl:col-span-3">
      <Tint v-if="!isEshopSet" class="text-sm" tag="div">
        <CommonLabel path="/BWF/shop/account/orderHistory/itemNumber" /><span>:&nbsp;</span
        >{{ lineItem?.payload?.productNumber ?? lineItem?.children?.[0]?.payload?.productNumber }}
      </Tint>

      <template v-if="itemOptions.length > 0">
        <template v-for="itemOption in itemOptions" :key="itemOption.group">
          <Tint>{{ itemOption.group }}:&nbsp;{{ itemOption.option }}&nbsp;</Tint>
        </template>
      </template>
    </div>
    <div v-if="!isSetChild" class="col-start-3 row-start-1 justify-self-end xl:col-start-4">
      <SharedPrice
        v-if="isConfigured && !isExchangeBox"
        :value="orderConfiguration[lineItem.id]?.quantity * lineItem.unitPrice"
        class="font-medium"
        has-asterisk
      />
      <SharedPrice
        v-else-if="!isExchangeBox && (returnItem?.refundAmount || totalPriceWithoutPromo)"
        :value="returnItem?.refundAmount || totalPriceWithoutPromo"
        class="font-medium"
        has-asterisk
      />
    </div>
    <template v-if="isExchangeBox">
      <OrderLineItemExchangeBoxChildren :line-item="lineItem" />
      <div class="notification is-blue col-span-full mb-0 mt-4 gap-4">
        <SvgIcon class="notification-icon" name="Tooltip" />
        <CommonLabel
          class="prose-black"
          path="BWF/shop/account/orderDetails/exchangeBox/exchangeBoxLink"
        />
      </div>
    </template>
    <div
      v-if="isEshopSet && (lineItem?.children?.length ?? 0) > 0"
      :class="{ 'col-start-2': !isEshopSet }"
      class="col-span-full mt-4 grid gap-2"
    >
      <!--            SET ITEM CHILDREN-->
      <template v-for="(childItem, index) of lineItem.children" :key="childItem.id">
        <OrderLineItem v-if="!isChildHidden(index)" :is-set-child="true" :line-item="childItem" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  OrderLineItem as OrderLineItemType,
  Product,
  PropertyGroupOptionCart,
} from "@shopware-pwa/types";
import { Tint } from "~/components/clarity-ui/Tint";
import { useOrderSelection } from "~/composables/shop/useOrderSelection";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import OrderLineItemExchangeBoxChildren from "~/templates/components/shop/account/OrderLineItemExchangeBoxChildren.vue";
import SharedPrice from "~/templates/components/shop/shared/SharedPrice.vue";
import OrderProductImage from "~/templates/components/shop/account/OrderProductImage.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";

export interface OrderLineItemProps {
  lineItem: OrderLineItemType;
  returnItem?: object;
  quantityChange?: boolean;
  isSetChild?: boolean;
  isConfigured?: boolean;
}

const { lineItem } = defineProps<OrderLineItemProps>();

const { orderConfiguration } = useOrderSelection();

const isEshopSet = computed(
  () =>
    (lineItem?.extensions?.hasOwnProperty("eshopSetData") ||
      lineItem?.customFields?.hasOwnProperty("eshopSetData")) ??
    false,
);

/**
 * return absolute price value as lineItem?.totalPrice might be negative but lineItem?.payload?.promotionDetails?.originalTotalPrice will always be positive
 */
const totalPriceWithoutPromo = computed(() =>
  Math.abs(lineItem?.payload?.promotionDetails?.originalTotalPrice ?? props.lineItem?.totalPrice),
);

const isExchangeBox = computed(() =>
  lineItem?.children?.[0]?.payload?.hasOwnProperty("contractPlan"),
);

const isSubscriptionProduct = computed(() =>
  lineItem?.children?.[0]?.payload?.hasOwnProperty("subscription"),
);

const itemOptions = computed<PropertyGroupOptionCart[]>(
  () => (lineItem.type === "product" && (lineItem?.payload as Product)?.options) || [],
);

const maxQty = ref(lineItem.quantity ?? 1);
const selectedQuantity: Ref<number> = ref(lineItem.quantity ?? "1");

/*
 * checks whether a child is the first item AND should be hidden
 * OR
 * ist a hidden Item/ SKU
 * */
const isChildHidden = (index: number) => {
  const child = lineItem?.children?.[index];
  return !!(
    ((index === 0 &&
      (lineItem?.extensions?.brita_eshop_set_hide_main_product_image?.value ?? false)) ||
      child?.customFields?.eshopSet?.eshopSetProductIsHidden) ??
    false
  );
};

watch(selectedQuantity, () => {
  // update selected quantity while making sure it is an integer
  orderConfiguration.value[lineItem?.id].quantity = parseInt(selectedQuantity.value);
});

const quantityOptions = computed(() => {
  const options: never[] = [];
  for (let i = 1; i <= maxQty.value; i++) {
    options.push({ value: i });
  }
  return options;
});
</script>
